import React, { useState, useRef } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  Grid,
  IconButton,
  MenuItem,
  Select,
  TextField,
  Typography,
  InputAdornment,
  Paper,
} from "@mui/material";
import { useFormik } from "formik";
import SaveAltIcon from "@mui/icons-material/SaveAlt";
import CloseIcon from "@mui/icons-material/Close";
import PersonAddAltOutlinedIcon from "@mui/icons-material/PersonAddAltOutlined";
import BusinessOutlinedIcon from "@mui/icons-material/BusinessOutlined";
import PersonOutlinedIcon from "@mui/icons-material/PersonOutlined";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import { useCreateCustomerMutation } from "../../../Api/attoDeskApi";
import { useNotifier } from "../../../Core/Notifier";
import { ICustomer } from "../../../Api/Interface/api.interface";
import { formatPhoneNumber } from "../../../Components/Validation/validationUtils";

const CANADIAN_PROVINCES_POPUP = [
  { code: "AB", name: "Alberta" }, { code: "BC", name: "British Columbia" },
  { code: "MB", name: "Manitoba" }, { code: "NB", name: "New Brunswick" },
  { code: "NL", name: "Newfoundland and Labrador" }, { code: "NS", name: "Nova Scotia" },
  { code: "NT", name: "Northwest Territories" }, { code: "NU", name: "Nunavut" },
  { code: "ON", name: "Ontario" }, { code: "PE", name: "Prince Edward Island" },
  { code: "QC", name: "Quebec" }, { code: "SK", name: "Saskatchewan" }, { code: "YT", name: "Yukon" },
];

const PROVINCE_CODE_MAP: Record<string, string> = {
  Alberta: "AB", "British Columbia": "BC", Manitoba: "MB", "New Brunswick": "NB",
  "Newfoundland and Labrador": "NL", "Nova Scotia": "NS", "Northwest Territories": "NT",
  Nunavut: "NU", Ontario: "ON", "Prince Edward Island": "PE", Quebec: "QC",
  Saskatchewan: "SK", Yukon: "YT",
};

interface NominatimResultPopup {
  display_name: string;
  address: { house_number?: string; road?: string; city?: string; town?: string; village?: string; county?: string; state?: string; postcode?: string; };
}

const TIME_SLOTS = [
  "Morning (8:00 AM – 10:00 AM)",
  "Late Morning (10:00 AM – 12:00 PM)",
  "Early Afternoon (12:00 PM – 2:00 PM)",
  "Afternoon (2:00 PM – 4:00 PM)",
  "Late Afternoon (4:00 PM – 6:00 PM)",
  "Evening (6:00 PM – 8:00 PM)",
  "Anytime",
  "Weekdays Only",
  "Weekends Only",
];

const fieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: 2, fontSize: 13, bgcolor: "#fff",
    "& fieldset": { borderColor: "#e2e8f0" },
    "&:hover fieldset": { borderColor: "#2f3891" },
    "&.Mui-focused fieldset": { borderColor: "#2f3891", borderWidth: 2 },
  },
};

const labelSx = { fontSize: 12, fontWeight: 600, color: "#475569", mb: 0.5, display: "block" };

const SectionCard = ({ icon, title, children }: { icon: React.ReactNode; title: string; children: React.ReactNode }) => (
  <Box sx={{ bgcolor: "#fff", borderRadius: 2.5, border: "1px solid #e2e8f0", overflow: "hidden" }}>
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, px: 2.5, py: 1.75, borderBottom: "1px solid #f1f5f9" }}>
      <Box sx={{
        width: 30, height: 30, borderRadius: 1.5,
        background: "linear-gradient(135deg,#2f3891,#2f3891)",
        display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
      }}>
        {icon}
      </Box>
      <Typography sx={{ fontWeight: 700, fontSize: 13, color: "#1e293b" }}>{title}</Typography>
    </Box>
    <Box sx={{ p: 2.5 }}>{children}</Box>
  </Box>
);

interface ITaxpopup {
  openModel?: boolean;
  handleCloseDialog: (close: boolean) => void;
  data?: ICustomer;
  onSuccess?: (newCustomer: any) => void;
}

const NewPopUpCustomer = ({ openModel = false, handleCloseDialog, data, onSuccess }: ITaxpopup) => {
  const [newCustomer, { isLoading }] = useCreateCustomerMutation();
  const { showErrorMessage, showMessage } = useNotifier();
  const handleClose = () => handleCloseDialog(false);
  const API_URL = process.env.REACT_APP_API_URL;

  // ── Duplicate validation state (per-field) ───────────────────────────────
  // Keys: "companyPhone", "mobilePhone", "companyEmail", "contactEmail"
  const [dupErrors, setDupErrors] = useState<Record<string, string>>({});
  const dupTimers = useRef<Record<string, ReturnType<typeof setTimeout> | null>>({});

  const setDupError = (field: string, msg: string) => {
    setDupErrors((prev) => {
      if (msg) return { ...prev, [field]: msg };
      const next = { ...prev }; delete next[field]; return next;
    });
  };

  const checkPhoneDuplicate = (field: string, phone: string) => {
    if (dupTimers.current[field]) clearTimeout(dupTimers.current[field]!);
    const trimmed = phone.trim();
    if (!trimmed) { setDupError(field, ""); return; }
    dupTimers.current[field] = setTimeout(async () => {
      try {
        const axios = (await import("axios")).default;
        const res = await axios.get(`${API_URL}/customer/check-phone`, {
          params: { phone: trimmed, exclude_id: data?.id },
          headers: { Authorization: `Bearer ${localStorage.getItem("token") || ""}` },
        });
        if (res.data?.exists) {
          const name = res.data?.client?.name || "another client";
          setDupError(field, `Phone number already in use by ${name}`);
        } else {
          setDupError(field, "");
        }
      } catch { /* silent */ }
    }, 450);
  };

  const checkEmailDuplicate = (field: string, email: string) => {
    if (dupTimers.current[field]) clearTimeout(dupTimers.current[field]!);
    const trimmed = email.trim();
    if (!trimmed) { setDupError(field, ""); return; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) { setDupError(field, ""); return; }
    dupTimers.current[field] = setTimeout(async () => {
      try {
        const axios = (await import("axios")).default;
        const res = await axios.get(`${API_URL}/customer/check-email`, {
          params: { email: trimmed, exclude_id: data?.id },
          headers: { Authorization: `Bearer ${localStorage.getItem("token") || ""}` },
        });
        if (res.data?.exists) {
          const name = res.data?.client?.name || "another client";
          setDupError(field, `Email already in use by ${name}`);
        } else {
          setDupError(field, "");
        }
      } catch { /* silent */ }
    }, 450);
  };

  const hasAnyDupError = () => Object.values(dupErrors).some((m) => !!m);

  // ── Address autocomplete ──────────────────────────────────────────────────
  const [addrSuggestionsPopup, setAddrSuggestionsPopup] = useState<Array<{ street: string; city: string; province: string; postalCode: string; display: string }>>([]);
  const [addrOpenPopup, setAddrOpenPopup] = useState(false);
  const [addrLoadingPopup, setAddrLoadingPopup] = useState(false);
  const addrTimerPopup = useRef<ReturnType<typeof setTimeout> | null>(null);

  const formik = useFormik({
    initialValues: {
      id: data?.id,
      customerCompanyName: data?.customerCompanyName || "",
      customerCompanyPhoneNumebr: data?.customerCompanyPhoneNumebr || "",
      customerCompanyEmail: data?.customerCompanyEmail || "",
      customerPreferTimeToCall: (data as any)?.customerPreferTimeToCall || "",
      customerFirstName: data?.customerFirstName || "",
      customerLastName: data?.customerLastName || "",
      customerMobileNum: data?.customerMobileNum || "",
      customerEmail: data?.customerEmail || "",
      customerDescription: data?.customerDescription || "",
      customerStreetNumber: data?.customerStreetNumber || "",
      customerStreetName: data?.customerStreetName || "",
      customerCity: data?.customerCity || "",
      customerProvince: data?.customerProvince || "",
      customerPostalCode: data?.customerPostalCode || "",
    },
    enableReinitialize: true,
    validate: (values) => {
      const errors: { [key: string]: string } = {};
      if (!values.customerCompanyName.trim() && !values.customerFirstName.trim())
        errors.customerCompanyName = "Enter a Name or a Company / School / Person Name";
      return errors;
    },
    onSubmit: async (values) => {
      if (hasAnyDupError()) {
        const firstErr = Object.values(dupErrors).find((m) => !!m);
        if (firstErr) showErrorMessage(firstErr);
        return;
      }
      try {
        const temData = {
          id: values.id,
          customerCompanyName: values.customerCompanyName,
          customerCompanyPhoneNumebr: values.customerCompanyPhoneNumebr,
          customerCompanyEmail: values.customerCompanyEmail,
          customerPreferTimeToCall: values.customerPreferTimeToCall,
          customerFirstName: values.customerFirstName,
          customerLastName: values.customerLastName,
          customerMobileNum: values.customerMobileNum,
          customerEmail: values.customerEmail,
          customerDescription: values.customerDescription,
          customerStreetNumber: values.customerStreetNumber,
          customerStreetName: values.customerStreetName,
          customerCity: values.customerCity,
          customerProvince: values.customerProvince,
          customerPostalCode: values.customerPostalCode,
        };
        if (!data) delete (temData as any).id;

        const response = await newCustomer(temData).unwrap();
        if (!response.status) {
          showErrorMessage(response.message);
        } else {
          showMessage(response.message);
          onSuccess && onSuccess(response.data);
          handleClose();
        }
      } catch {
        showErrorMessage("Something went wrong");
      }
    },
  });

  const searchAddressPopup = (val: string) => {
    formik.setFieldValue("customerStreetName", val);
    if (addrTimerPopup.current) clearTimeout(addrTimerPopup.current);
    if (val.trim().length < 4) { setAddrSuggestionsPopup([]); setAddrOpenPopup(false); return; }
    setAddrOpenPopup(true);
    addrTimerPopup.current = setTimeout(async () => {
      setAddrLoadingPopup(true);
      try {
        const url = `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(val)}&countrycodes=ca&addressdetails=1&limit=6`;
        const res = await fetch(url, { headers: { "Accept-Language": "en" } });
        const data: NominatimResultPopup[] = await res.json();
        setAddrSuggestionsPopup(data.map(item => ({
          street: [item.address?.house_number, item.address?.road].filter(Boolean).join(" ") || item.display_name.split(",")[0],
          city: item.address?.city || item.address?.town || item.address?.village || item.address?.county || "",
          province: PROVINCE_CODE_MAP[item.address?.state || ""] || item.address?.state || "",
          postalCode: item.address?.postcode || "",
          display: item.display_name,
        })));
      } catch { setAddrSuggestionsPopup([]); } finally { setAddrLoadingPopup(false); }
    }, 450);
  };

  const applyAddressPopup = (s: { street: string; city: string; province: string; postalCode: string }) => {
    const parts = s.street.split(" ");
    const streetNum = /^\d+/.test(parts[0]) ? parts[0] : "";
    const streetName = streetNum ? parts.slice(1).join(" ") : s.street;
    formik.setFieldValue("customerStreetNumber", streetNum);
    formik.setFieldValue("customerStreetName", streetName);
    formik.setFieldValue("customerCity", s.city);
    formik.setFieldValue("customerProvince", s.province);
    formik.setFieldValue("customerPostalCode", s.postalCode);
    setAddrSuggestionsPopup([]);
    setAddrOpenPopup(false);
  };

  const fieldSxPopup = {
    "& .MuiOutlinedInput-root": {
      borderRadius: 2, fontSize: 13, bgcolor: "#fff",
      "& fieldset": { borderColor: "#e2e8f0" },
      "&:hover fieldset": { borderColor: "#2f3891" },
      "&.Mui-focused fieldset": { borderColor: "#2f3891", borderWidth: 2 },
    },
  };

  const labelSxPopup = { fontSize: 12, fontWeight: 600, color: "#475569", mb: 0.5, display: "block" };

  return (
    <Dialog open={openModel} onClose={handleClose} maxWidth="sm" fullWidth
      PaperProps={{ sx: { borderRadius: 3, overflow: "hidden" } }}>

      {/* ── Header ── */}
      <Box sx={{
        background: "linear-gradient(135deg,#2f3891 0%,#2f3891 100%)",
        px: 3, py: 2,
        display: "flex", alignItems: "center", justifyContent: "space-between",
      }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Box sx={{ width: 36, height: 36, borderRadius: 2, bgcolor: "rgba(255,255,255,0.15)", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <PersonAddAltOutlinedIcon sx={{ color: "#fff", fontSize: 20 }} />
          </Box>
          <Box>
            <Typography sx={{ color: "#fff", fontWeight: 700, fontSize: 15, lineHeight: 1 }}>New Client</Typography>
            <Typography sx={{ color: "rgba(255,255,255,0.7)", fontSize: 11, mt: 0.3 }}>Add a new client to your directory</Typography>
          </Box>
        </Box>
        <IconButton size="small" onClick={handleClose}
          sx={{ color: "rgba(255,255,255,0.7)", "&:hover": { color: "#fff", bgcolor: "rgba(255,255,255,0.1)" } }}>
          <CloseIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </Box>

      <DialogContent sx={{ p: 2.5, bgcolor: "#f8fafc" }}>
        <form onSubmit={formik.handleSubmit}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>

            {/* ── Section 1: Company / School / Person ── */}
            <SectionCard
              icon={<BusinessOutlinedIcon sx={{ color: "#fff", fontSize: 15 }} />}
              title="Company / School / Person Information"
            >
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography sx={labelSx}>Name <span style={{ color: "#ef4444" }}>*</span></Typography>
                  <TextField
                    fullWidth size="small" placeholder="Enter Name"
                    {...formik.getFieldProps("customerCompanyName")}
                    error={formik.touched.customerCompanyName && Boolean(formik.errors.customerCompanyName)}
                    helperText={formik.touched.customerCompanyName && formik.errors.customerCompanyName}
                    sx={fieldSx}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Typography sx={labelSx}>Phone Number</Typography>
                  <TextField
                    fullWidth size="small" placeholder="(416) 555-0100"
                    value={formik.values.customerCompanyPhoneNumebr}
                    onChange={(e) => {
                      const val = formatPhoneNumber(e.target.value);
                      formik.setFieldValue("customerCompanyPhoneNumebr", val);
                      checkPhoneDuplicate("companyPhone", val);
                    }}
                    error={!!dupErrors.companyPhone}
                    helperText={dupErrors.companyPhone || ""}
                    sx={fieldSx}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Typography sx={labelSx}>E-mail</Typography>
                  <TextField
                    fullWidth size="small" type="email" placeholder="Enter E-mail"
                    value={formik.values.customerCompanyEmail}
                    onChange={(e) => {
                      formik.setFieldValue("customerCompanyEmail", e.target.value);
                      checkEmailDuplicate("companyEmail", e.target.value);
                    }}
                    error={!!dupErrors.companyEmail}
                    helperText={dupErrors.companyEmail || ""}
                    sx={fieldSx}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Typography sx={labelSx}>Prefer Time to Call</Typography>
                  <Select
                    fullWidth size="small" displayEmpty
                    value={formik.values.customerPreferTimeToCall}
                    onChange={(e) => formik.setFieldValue("customerPreferTimeToCall", e.target.value)}
                    sx={{
                      borderRadius: 2, fontSize: 13, bgcolor: "#fff",
                      "& fieldset": { borderColor: "#e2e8f0" },
                      "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#2f3891" },
                      "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#2f3891", borderWidth: 2 },
                    }}
                  >
                    <MenuItem value="" disabled sx={{ fontSize: 13, color: "#94a3b8" }}>Select preferred time</MenuItem>
                    {TIME_SLOTS.map((slot) => (
                      <MenuItem key={slot} value={slot} sx={{ fontSize: 13 }}>{slot}</MenuItem>
                    ))}
                  </Select>
                </Grid>
              </Grid>
            </SectionCard>

            {/* ── Section 2: Contact Information ── */}
            <SectionCard
              icon={<PersonOutlinedIcon sx={{ color: "#fff", fontSize: 15 }} />}
              title="Contact Information"
            >
              <Box sx={{ mb: 2, px: 1.5, py: 1, bgcolor: "#f8fafc", borderRadius: 1.5, border: "1px solid #e2e8f0" }}>
                <Typography sx={{ fontSize: 11, color: "#94a3b8", fontWeight: 500 }}>
                  All fields in this section are optional. Fill in contact details if available.
                </Typography>
              </Box>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography sx={labelSx}>
                    Name <Box component="span" sx={{ fontSize: 11, fontWeight: 400, color: "#94a3b8" }}>(optional)</Box>
                  </Typography>
                  <TextField
                    fullWidth size="small" placeholder="Enter Name"
                    {...formik.getFieldProps("customerFirstName")}
                    sx={fieldSx}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Typography sx={labelSx}>
                    Mobile Number <Box component="span" sx={{ fontSize: 11, fontWeight: 400, color: "#94a3b8" }}>(optional)</Box>
                  </Typography>
                  <TextField
                    fullWidth size="small" placeholder="(416) 555-0100"
                    value={formik.values.customerMobileNum}
                    onChange={(e) => {
                      const val = formatPhoneNumber(e.target.value);
                      formik.setFieldValue("customerMobileNum", val);
                      checkPhoneDuplicate("mobilePhone", val);
                    }}
                    error={!!dupErrors.mobilePhone}
                    helperText={dupErrors.mobilePhone || ""}
                    sx={fieldSx}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Typography sx={labelSx}>
                    E-mail <Box component="span" sx={{ fontSize: 11, fontWeight: 400, color: "#94a3b8" }}>(optional)</Box>
                  </Typography>
                  <TextField
                    fullWidth size="small" type="email" placeholder="Enter E-mail"
                    value={formik.values.customerEmail}
                    onChange={(e) => {
                      formik.setFieldValue("customerEmail", e.target.value);
                      checkEmailDuplicate("contactEmail", e.target.value);
                    }}
                    error={!!dupErrors.contactEmail}
                    helperText={dupErrors.contactEmail || ""}
                    sx={fieldSx}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Typography sx={labelSx}>
                    Notes <Box component="span" sx={{ fontSize: 11, fontWeight: 400, color: "#94a3b8" }}>(optional)</Box>
                  </Typography>
                  <TextField
                    fullWidth size="small" multiline rows={3}
                    placeholder="Enter notes…"
                    {...formik.getFieldProps("customerDescription")}
                    sx={fieldSx}
                  />
                </Grid>
              </Grid>
            </SectionCard>

            {/* ── Section 3: Address (optional) ── */}
            <SectionCard
              icon={<LocationOnOutlinedIcon sx={{ color: "#fff", fontSize: 15 }} />}
              title="Address Information"
            >
              <Box sx={{ mb: 2, px: 1.5, py: 1, bgcolor: "#f8fafc", borderRadius: 1.5, border: "1px solid #e2e8f0" }}>
                <Typography sx={{ fontSize: 11, color: "#94a3b8", fontWeight: 500 }}>
                  Optional. Start typing address to get auto-fill suggestions.
                </Typography>
              </Box>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={8}>
                  <Typography sx={labelSxPopup}>
                    Address <Box component="span" sx={{ fontSize: 11, fontWeight: 400, color: "#94a3b8" }}>(optional)</Box>
                  </Typography>
                  <Box sx={{ position: "relative" }}>
                    <TextField
                      fullWidth size="small"
                      placeholder="Start typing street address…"
                      value={`${formik.values.customerStreetNumber ? formik.values.customerStreetNumber + " " : ""}${formik.values.customerStreetName}`}
                      onChange={(e) => searchAddressPopup(e.target.value)}
                      onFocus={() => { if (addrSuggestionsPopup.length > 0) setAddrOpenPopup(true); }}
                      onBlur={() => setTimeout(() => setAddrOpenPopup(false), 200)}
                      InputProps={{
                        endAdornment: addrLoadingPopup ? <InputAdornment position="end"><CircularProgress size={14} /></InputAdornment> : undefined,
                        sx: { bgcolor: "#fff" },
                      }}
                      sx={fieldSxPopup}
                    />
                    {addrOpenPopup && addrSuggestionsPopup.length > 0 && (
                      <Paper sx={{
                        position: "absolute", top: "100%", left: 0, right: 0, zIndex: 9999,
                        maxHeight: 220, overflowY: "auto", border: "1px solid #e2e8f0",
                        borderRadius: 1.5, boxShadow: "0 8px 24px rgba(0,0,0,0.12)", mt: 0.5,
                      }}>
                        {addrSuggestionsPopup.map((s, i) => (
                          <Box key={i} onMouseDown={() => applyAddressPopup(s)}
                            sx={{ px: 2, py: 1, cursor: "pointer", "&:hover": { bgcolor: "#eff6ff" }, borderBottom: "1px solid #f1f5f9" }}>
                            <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#1e293b" }}>{s.street}</Typography>
                            <Typography sx={{ fontSize: 11, color: "#64748b" }} noWrap>{s.display}</Typography>
                          </Box>
                        ))}
                      </Paper>
                    )}
                  </Box>
                </Grid>

                <Grid item xs={12} sm={4}>
                  <Typography sx={labelSxPopup}>City <Box component="span" sx={{ fontSize: 11, fontWeight: 400, color: "#94a3b8" }}>(optional)</Box></Typography>
                  <TextField fullWidth size="small" placeholder="City"
                    {...formik.getFieldProps("customerCity")} sx={fieldSxPopup} />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Typography sx={labelSxPopup}>Province <Box component="span" sx={{ fontSize: 11, fontWeight: 400, color: "#94a3b8" }}>(optional)</Box></Typography>
                  <Select fullWidth size="small" displayEmpty
                    value={formik.values.customerProvince}
                    onChange={(e) => formik.setFieldValue("customerProvince", e.target.value)}
                    sx={{
                      borderRadius: 2, fontSize: 13, bgcolor: "#fff",
                      "& fieldset": { borderColor: "#e2e8f0" },
                      "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#2f3891" },
                      "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#2f3891", borderWidth: 2 },
                    }}>
                    <MenuItem value="" sx={{ fontSize: 13, color: "#94a3b8" }}>Select Province</MenuItem>
                    {CANADIAN_PROVINCES_POPUP.map((p) => (
                      <MenuItem key={p.code} value={p.code} sx={{ fontSize: 13 }}>{p.name}</MenuItem>
                    ))}
                  </Select>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Typography sx={labelSxPopup}>Postal Code <Box component="span" sx={{ fontSize: 11, fontWeight: 400, color: "#94a3b8" }}>(optional)</Box></Typography>
                  <TextField fullWidth size="small" placeholder="e.g. M1A 1A1"
                    {...formik.getFieldProps("customerPostalCode")}
                    onChange={(e) => formik.setFieldValue("customerPostalCode", e.target.value.toUpperCase())}
                    sx={fieldSxPopup} />
                </Grid>
              </Grid>
            </SectionCard>

            {/* ── Actions ── */}
            <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1.5 }}>
              <Button variant="outlined" startIcon={<CloseIcon />} onClick={handleClose}
                sx={{
                  textTransform: "none", fontWeight: 600, fontSize: 13, borderRadius: 2,
                  borderColor: "#e2e8f0", color: "#64748b",
                  "&:hover": { borderColor: "#94a3b8", bgcolor: "#f8fafc" },
                }}>
                Cancel
              </Button>
              <Button
                variant="contained"
                startIcon={isLoading
                  ? <CircularProgress size={14} sx={{ color: "#fff" }} />
                  : <SaveAltIcon />}
                onClick={() => formik.handleSubmit()}
                disabled={isLoading}
                sx={{
                  textTransform: "none", fontWeight: 700, fontSize: 13, borderRadius: 2,
                  background: "linear-gradient(135deg,#2f3891,#2f3891)",
                  boxShadow: "0 2px 8px rgba(99,102,241,0.35)", px: 3,
                  "&:hover": { background: "linear-gradient(135deg,#2f3891,#2f3891)" },
                  "&:disabled": { background: "#e2e8f0", color: "#94a3b8", boxShadow: "none" },
                }}>
                Save Client
              </Button>
            </Box>

          </Box>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default NewPopUpCustomer;
